import { z } from "zod";

/**
 * DEĞİŞMEZ sözleşme versiyonu (CLOS-133 `[088]`).
 *
 * ### ⚠ Versiyon bir KAYIT değil, bir ANDIR
 *
 * Bir versiyon "sözleşmenin şu andaki hâli" değil, *"şu tarihte şu kişinin ürettiği hâl"*
 * demektir. Bu yüzden içeriği referansla değil KOPYAYLA saklanıyor ve satır hiç
 * güncellenmiyor: kart *"oluşturulmuş versiyon içeriği ve tarihsel kimliği ÜZERİNE
 * YAZILARAK DEĞİŞTİRİLEMEZ"* diyor.
 *
 * ### ⚠ Rol yönetimi BU KARTTA DEĞİL
 *
 * "Güncel", "karşı tarafa sunulan", "onaylı" gibi roller CLOS-136'nın konusu ve rol
 * değişikliği versiyon İÇERİĞİNİ değiştirmez. Bu modele bir `isCurrent` bayrağı koymak,
 * değişmez bir satırı değişebilir yapardı.
 */

export const CONTRACT_VERSION_SOURCES = [
  "file_upload",
  "editor",
  "counterparty_upload",
  "version_copy",
  "template",
] as const;
export type ContractVersionSource = (typeof CONTRACT_VERSION_SOURCES)[number];

export const CONTRACT_VERSION_SOURCE_NAMES: Record<ContractVersionSource, string> = {
  /**
   * Bir ŞABLONDAN doğan ilk versiyon (CLOS-130 `[112]`).
   *
   * ⚠ `version_copy` DEĞİL: kaynağı başka bir sözleşme versiyonu değil, kütüphanedeki bir
   * kalıp. İkisini aynı ada sıkıştırsaydık "bu metin hangi kalıptan geldi?" sorusu bir daha
   * cevaplanamazdı.
   */
  template: "Şablondan",
  file_upload: "Dosya yükleme",
  editor: "Uygulama içi editör",
  counterparty_upload: "Karşı taraftan gelen revizyon",
  /**
   * Geçmiş bir versiyonun içeriğinden başlatılan çalışma (CLOS-142 `[097]`).
   *
   * ⚠ `editor` DEĞİL: metni kimse yazmadı, kopyalandı. Editör diye işaretleseydik geçmiş,
   * o gün kimsenin yapmadığı bir yazımı birine mal ederdi. Hangi versiyondan geldiği
   * `baseVersionId`'de duruyor.
   */
  version_copy: "Eski versiyondan kopyalandı",
};

/**
 * Bütünlük özeti biçimi: `sha256:<64 hex>` ya da `unknown:<sebep>`.
 *
 * ⚠ "Bilinmiyor" AÇIKÇA yazılıyor. Özeti olmayan bir satıra sahte bir hash yazsaydık
 * bütünlük doğrulaması "bozulmamış" der ve kontrolün kendisi yalan söylerdi.
 */
export const CONTENT_HASH_PATTERN = /^(sha256:[0-9a-f]{64}|unknown:[a-z0-9-]{1,64})$/;

export function isKnownContentHash(hash: string): boolean {
  return hash.startsWith("sha256:");
}

export type ContractVersionIntegrity =
  /** Özet yeniden hesaplandı ve TUTTU. */
  | "verified"
  /** Özet tutmadı: içerik oluşturulduğundan beri değişmiş. */
  | "mismatch"
  /** Kaynak içerik artık okunamıyor (silinmiş, karantinada ya da saklama süresi dolmuş). */
  | "source_unavailable"
  /** Bu satırın gerçek bir özeti hiç kaydedilmemiş (`[088]` öncesi). */
  | "unknown";

export const CONTRACT_VERSION_INTEGRITY_NAMES: Record<ContractVersionIntegrity, string> = {
  verified: "Bütünlük doğrulandı",
  mismatch: "İçerik değişmiş görünüyor",
  source_unavailable: "Kaynak içerik erişilemiyor",
  unknown: "Bütünlük özeti kaydedilmemiş",
};

export type ContractVersionSummary = {
  id: string;
  contractId: string;
  versionNumber: number;
  sourceType: ContractVersionSource;
  baseVersionId: string | null;
  contentHash: string;
  /**
   * Bu versiyon UYGULAMA İÇİ METİN taşıyor mu (BUG-020 · Mega E2E'de ölçüldü).
   *
   * ⚠ `sourceType`'tan ÇIKARILAMAZ ve çıkarmak iki yönde de yanlış sonuç verir. AI revizyon
   * paneli `sourceType === "editor"` süzüyordu; şablondan doğan sözleşmelerde panel HİÇ
   * kullanılamıyordu. Ama tersi de doğru: şablon versiyonu dosya da olabiliyor
   * (`fromTemplate.ts`: `document: storageObjectId ? null : belge`), yani "template
   * olanlar metinlidir" demek de yanlış olurdu.
   *
   * Gerçeği yalnız satırın kendisi biliyor; bu yüzden sunucu SÖYLÜYOR, istemci tahmin
   * etmiyor.
   */
  hasDocument: boolean;
  /** Dosya versiyonlarında dolu; editör versiyonlarında `null`. */
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  /** İçerik hâlâ okunabiliyor mu — `sourcePurgedAt` doluysa hayır. */
  sourcePurgedAt: string | null;
  retainedUntil: string | null;
  createdBy: string;
  /** Oluşturma ANINDAKİ ad; sonradan değişse de geçmiş değişmiyor. */
  createdByName: string;
  createdAt: string;
};

/**
 * YENİ VERSİYON isteği (CLOS-134 `[089]`).
 *
 * ⚠ İki kaynak dalı TEK şemada ayrışıyor. Ayrı iki uç açsaydık, kartın *"ayrı ve daha zayıf
 * bir yazma endpointi oluşturmaz"* kuralı ilk günden delinir ve yetki/tenant/audit
 * kontrolleri iki yerde ayrı ayrı bakım isterdi.
 */
export const createContractVersionSchema = z.discriminatedUnion("source", [
  z.object({
    source: z.literal("editor"),
    /**
     * Kullanıcının GÖRDÜĞÜ çalışma metni sürümü.
     *
     * ⚠ Zorunlu: hangi hâli versiyonladığını söylemeyen bir istek, arada değişmiş bir metni
     * kullanıcının okuduğu metin diye tarihe geçirebilirdi.
     */
    contentRevision: z.coerce.number().int().min(1, "Geçerli bir metin sürümü gereklidir"),
    baseVersionId: z.uuid().nullish(),
  }),
  z.object({
    source: z.literal("file_upload"),
    storageObjectId: z.uuid("Geçerli bir dosya seçilmelidir"),
    baseVersionId: z.uuid().nullish(),
  }),
  /**
   * Geçmiş versiyondan yeni çalışma başlatma (CLOS-142 `[097]`).
   *
   * ⚠ `baseVersionId` YOK: baz zaten kaynağın kendisi ve iki ayrı alan taşımak, ikisinin
   * farklı olduğu bir istek üretilebilmesi demekti — "şu versiyondan kopyala ama bambaşka
   * bir versiyonun üzerine kurulmuş say" gibi anlamsız ve izi bozan bir kayıt.
   */
  z.object({
    source: z.literal("version_copy"),
    sourceVersionId: z.uuid("Geçerli bir kaynak versiyon seçilmelidir"),
  }),
]);
export type CreateContractVersionInput = z.infer<typeof createContractVersionSchema>;

/**
 * VERSİYON GEÇMİŞİ sorgusu (CLOS-135 `[090]`).
 *
 * ⚠ Sıralama seçeneği YOK ve bu bilinçli: versiyon geçmişi kronolojiktir. Kullanıcıya
 * "başlığa göre sırala" gibi bir seçenek sunmak, tarihsel bir kaydı bir listeymiş gibi
 * göstermek olurdu. Yön sabit: EN YENİ önce.
 */
export const contractVersionListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ContractVersionListQuery = z.infer<typeof contractVersionListQuerySchema>;

/**
 * Listede ve detayda GÖSTERİLEN versiyon.
 *
 * ⚠ `createdBy` (ham kullanıcı kimliği) ve depolama adresi YOK. Kart: *"yetkisiz kullanıcıya
 * GEREKSİZ KİŞİSEL BİLGİ açılmaz"* ve *"liste endpoint'i GİZLİ DEPOLAMA ADRESİ döndürmez"* —
 * içerik teslimi ayrı bir güvenli görüntüleme sözleşmesinin işi.
 */
export type ContractVersionView = {
  id: string;
  versionNumber: number;
  sourceType: ContractVersionSource;
  baseVersionId: string | null;
  baseVersionNumber: number | null;
  /** Oluşturma anındaki görünen ad; hesap kapansa da korunuyor. */
  createdByName: string;
  createdAt: string;
  fileName: string | null;
  sizeBytes: number | null;
  /** İçerik hâlâ erişilebilir mi (kaynak kaldırılmışsa hayır). */
  sourceAvailable: boolean;
  /**
   * Uygulama içi METİN taşıyor mu — `sourceType`'tan çıkarılamaz (BUG-020).
   * Gerekçe `ContractVersionSummary.hasDocument` yanında.
   */
  hasDocument: boolean;
  contentHash: string;
};

export type ContractVersionDetailView = ContractVersionView & {
  /** Detayda bütünlük DOĞRULANIYOR; listede satır başına hash hesaplamak pahalı olurdu. */
  integrity: ContractVersionIntegrity;
  /** Uygulama içi metin versiyonlarında madde sayısı; dosya versiyonlarında `null`. */
  clauseCount: number | null;
};

/**
 * VERSİYON ROLLERİ (CLOS-136 `[091]`).
 *
 * ⚠ Üçü AYRI kavram. Kart: *"biri diğerinin OTOMATİK EŞ ANLAMLISI DEĞİLDİR."* Bir metni
 * karşı tarafa göndermek onu onaylı yapmaz; onaylamak da otomatik olarak çalışma versiyonu
 * yapmaz — müzakere devam ederken onaylı metin sabit kalabilmeli.
 */
export const CONTRACT_VERSION_ROLES = [
  "current_working",
  "shared_with_counterparty",
  "approved",
] as const;
export type ContractVersionRole = (typeof CONTRACT_VERSION_ROLES)[number];

export const CONTRACT_VERSION_ROLE_NAMES: Record<ContractVersionRole, string> = {
  current_working: "Güncel çalışma versiyonu",
  shared_with_counterparty: "Karşı tarafa sunulan versiyon",
  approved: "Onaylı versiyon",
};

/**
 * Her rolün DEĞİŞTİRME yetkisi ayrı (kartın kuralı: *"her rol türünün ... permission'ı
 * sunucu tarafında AYRIŞTIRILIR"*). Anahtarlar mevcut katalogdan; yeni yetki anahtarı
 * açmak yetki yüzeyini değiştirir ve kendi kartını ister.
 */
export const CONTRACT_VERSION_ROLE_PERMISSIONS: Record<ContractVersionRole, string> = {
  current_working: "contract.edit",
  shared_with_counterparty: "contract.share",
  approved: "contract.approve",
};

/**
 * Rol atama girdisi.
 *
 * ⚠ `expectedVersionId` ZORUNLU (rol boşsa `null`): kart *"aynı rol için eşzamanlı
 * değişiklikler KAYBOLAN GÜNCELLEME üretmez"* diyor. Beklenen önceki değeri söylemeyen bir
 * istek, başkasının az önce işaretlediği versiyonu sessizce ezerdi.
 */
export const setContractVersionRoleSchema = z.object({
  versionId: z.uuid("Geçerli bir versiyon seçilmelidir"),
  expectedVersionId: z.uuid().nullable(),
});
export type SetContractVersionRoleInput = z.infer<typeof setContractVersionRoleSchema>;

export type ContractVersionRoleView = {
  role: ContractVersionRole;
  versionId: string;
  versionNumber: number;
  assignedByName: string;
  assignedAt: string;
};

/**
 * KARŞI TARAFTAN gelen revizyonu yükleme (CLOS-137 `[092]`).
 *
 * ⚠ `sourcePartyId` ZORUNLU: belgenin kimden geldiği, onu YÜKLEYEN kullanıcıdan ayrı bir
 * gerçek. Kart: *"kullanıcı karşı tarafın aktörü gibi gösterilmez."* Tek alanda tutsaydık
 * geçmiş, karşı tarafın revizyonunu bizim çalışanımızın yazdığını söylerdi.
 *
 * ⚠ `markAsCounterpartyVersion` VARSAYILAN DEĞİL, açık bir işaret. Kart: *"yeni versiyon
 * AÇIK İŞLEM sonucunda ... karşı taraf versiyonu rolüne bağlanabilir."* Otomatik yapsaydık
 * bir dosya yüklemek, sözleşmenin hangi metninin karşı tarafa ait sayıldığını sessizce
 * değiştirirdi.
 */
export const uploadCounterpartyRevisionSchema = z.object({
  storageObjectId: z.uuid("Geçerli bir dosya seçilmelidir"),
  sourcePartyId: z.uuid("Belgeyi gönderen taraf seçilmelidir"),
  baseVersionId: z.uuid().nullish(),
  markAsCounterpartyVersion: z.boolean().default(false),
  /** Rol işaretleniyorsa beklenen önceki değer de gerekiyor (`[091]` eşzamanlılık kuralı). */
  expectedCounterpartyVersionId: z.uuid().nullish(),
});
export type UploadCounterpartyRevisionInput = z.infer<typeof uploadCounterpartyRevisionSchema>;

/**
 * DOCX İÇE AKTARIMINDA KAYBOLANLAR (CLOS-1268 — kanon §38).
 *
 * ### ⚠ Neden kullanıcıya söyleniyor
 *
 * Kanon §38: *"DOCX unsupported element sessizce kaybolmaz."* Ölçüldü — karşı taraftan
 * gelen izlenen değişiklikli bir dosyada silmeler metinden düşüyor, eklemeler kabul
 * edilmiş gibi metne giriyor ve Word yorumları kayboluyordu. Hiçbiri bildirilmiyordu.
 *
 * ### ⚠ REDDETMEK DEĞİL, SÖYLEMEK
 *
 * Redline'lı dosyayı reddetmek müzakerenin en sık hâlini kırardı. Dosya alınıyor; kullanıcı
 * ne kaybettiğini öğreniyor.
 *
 * ### ⚠ Metin BURADA, ekranda değil
 *
 * Uyarı cümlesi şemada (kriter 6): arayüz kendi cümlesini uydurursa aynı kayıp iki ekranda
 * iki farklı şey anlatır.
 */
export const DOCX_ICE_AKTARIM_KAYIPLARI = [
  "blok_kimligi_yok",
  "madde_kimligi_yok",
  "tablo",
  "izlenen_degisiklik",
  "native_yorum",
  "numaralandirma",
] as const;
export type DocxIceAktarimKaybi = (typeof DOCX_ICE_AKTARIM_KAYIPLARI)[number];

export const DOCX_ICE_AKTARIM_KAYIP_UYARILARI: Record<DocxIceAktarimKaybi, string> = {
  blok_kimligi_yok:
    "Dosyadaki bazı paragraflar Closer kimliği taşımıyor; bunlar değişiklik karşılaştırmasında yeni paragraf sayılır.",
  madde_kimligi_yok:
    "Dosyadaki bazı maddeler Closer kimliği taşımıyor; bunlar değişiklik karşılaştırmasında yeni madde sayılır.",
  tablo: "Dosyadaki tablolar içe aktarılmadı; sözleşme metnine tablo olarak eklenemiyor.",
  izlenen_degisiklik:
    "Bu dosya izlenen değişiklikler taşıyor; metin, değişiklikler UYGULANMIŞ hâliyle alındı. Karşı tarafın önerisi ile kabul edilmiş metin arasındaki ayrım dosyadan korunmadı.",
  native_yorum:
    "Dosyadaki Word yorumları içe aktarılmadı; Closer yorumları sözleşmenin kendi yorum bölümünde tutulur.",
  /**
   * ⚠ Metin "seviye" diyor, "numId" demiyor: kullanıcı belgesinde bir liste kimliği
   * görmez, girintili maddeler görür (kriter 6 · kullanıcı dili).
   */
  numaralandirma:
    "Dosyadaki çok seviyeli ya da özel numaralandırma listeleri düz paragraf olarak alındı; girinti seviyeleri sözleşme metnine taşınmadı. Word dosyasının kendisi değişmedi.",
};
